"use client";

import React from "react";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { useCart } from "@/src/contexts/CartContext";

const FREE_DELIVERY_THRESHOLD = 499;

export const FloatingCart: React.FC = () => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const prefix = searchParams.get("prefix") || "";
  const { getItemCount, getTotal } = useCart();

  const totalItems = getItemCount();
  const totalPrice = getTotal();
  const isFree = totalPrice >= FREE_DELIVERY_THRESHOLD;
  const progress = Math.min((totalPrice / FREE_DELIVERY_THRESHOLD) * 100, 100);
  const remaining = Math.max(FREE_DELIVERY_THRESHOLD - totalPrice, 0);

  if (totalItems === 0) return null;
  if (pathname.includes("/checkout") || pathname.includes("/profile") || pathname.includes("/orders")) return null;

  return (
    <div
      className="fixed bottom-6 left-1/2 z-[60] -translate-x-1/2"
      style={{ width: "min(560px, calc(100% - 32px))" }}
    >
      <div
        className="rounded-2xl p-3.5 pl-[18px]"
        style={{
          background: "#E4604E",
          color: "#fff",
          boxShadow:
            "0 16px 48px rgba(20,35,18,0.35), 0 2px 0 rgba(255,255,255,0.05) inset",
        }}
      >
        {/* Top row */}
        <div className="flex items-center gap-3.5">
          {/* Cart icon */}
          <div
            className="flex shrink-0 items-center justify-center rounded-xl"
            style={{ width: 40, height: 40, background: "rgba(255,255,255,0.12)" }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
              <path d="M3 6h18" />
              <path d="M16 10a4 4 0 01-8 0" />
            </svg>
          </div>

          {/* Info */}
          <div className="flex flex-1 min-w-0 flex-col gap-0.5">
            <span className="text-sm font-bold leading-none tracking-[-0.2px]">
              {totalItems} item{totalItems !== 1 ? "s" : ""} added
            </span>
            <span className="text-[11.5px] font-medium opacity-75">
              Total · ₹{totalPrice}
            </span>
          </div>

          {/* CTA */}
          <button
            onClick={() =>
              router.push(`/shop/checkout${prefix ? `?prefix=${prefix}` : ""}`)
            }
            className="shrink-0 flex items-center gap-1.5 rounded-full px-4 py-2.5 text-[13.5px] font-bold transition"
            style={{ background: "#fff", color: "#E4604E" }}
            onMouseEnter={e => (e.currentTarget.style.transform = "translateX(2px)")}
            onMouseLeave={e => (e.currentTarget.style.transform = "")}
          >
            View Cart
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Free delivery progress — only show "add more" nudge, not unlocked message */}
        {!isFree && (
          <div
            className="mt-2.5 pt-2.5"
            style={{ borderTop: "1px solid rgba(255,255,255,0.12)" }}
          >
            <p className="mb-1.5 flex items-center gap-1.5 text-[11.5px] font-medium" style={{ color: "rgba(255,255,255,0.85)" }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 3h15v13H1z" /><path d="M16 8h4l3 3v5h-7" />
                <circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />
              </svg>
              Add{" "}
              <strong style={{ color: "#fff" }}>₹{remaining}</strong>{" "}
              more for{" "}
              <strong>FREE delivery</strong>
            </p>

            {/* Progress bar */}
            <div
              className="overflow-hidden rounded-full"
              style={{ height: 4, background: "rgba(255,255,255,0.12)" }}
            >
              <div
                className="h-full rounded-full"
                style={{
                  width: `${progress}%`,
                  background: "linear-gradient(90deg, #fff, rgba(255,255,255,0.7))",
                  transition: "width 320ms cubic-bezier(.22,1,.36,1)",
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
